/**
 * Minimal ADF (Atlassian Document Format) to HTML converter.
 * Handles paragraph, text, bulletList, orderedList, hardBreak, heading.
 * Output must be sanitized with DOMPurify before use in dangerouslySetInnerHTML.
 */
export function adfToHtml(node) {
  if (!node) return '';
  if (typeof node === 'string') return escapeHtml(node);
  if (Array.isArray(node)) return node.map(adfToHtml).join('');

  const { type, content, text, marks } = node;
  let inner = '';
  if (text != null) {
    inner = marks && marks.length ? applyMarks(escapeHtml(text), marks) : escapeHtml(text);
  }
  if (content && content.length) {
    inner += content.map(adfToHtml).join('');
  }

  switch (type) {
    case 'doc':
      return inner;
    case 'paragraph':
      return `<p>${inner || '<br>'}</p>`;
    case 'heading':
      return `<h${node.attrs?.level || 2}>${inner}</h${node.attrs?.level || 2}>`;
    case 'bulletList':
      return `<ul>${inner}</ul>`;
    case 'orderedList':
      return `<ol>${inner}</ol>`;
    case 'listItem':
      return `<li>${inner}</li>`;
    case 'hardBreak':
      return '<br>';
    case 'text':
      return inner;
    default:
      return inner ? `<p>${inner}</p>` : '';
  }
}

function applyMarks(text, marks) {
  let out = text;
  for (const m of marks) {
    if (m.type === 'strong') out = `<strong>${out}</strong>`;
    else if (m.type === 'em') out = `<em>${out}</em>`;
    else if (m.type === 'code') out = `<code>${out}</code>`;
    else if (m.type === 'link') out = `<a href="${escapeAttr(m.attrs?.href || '#')}">${out}</a>`;
  }
  return out;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Convert description/comment body from API (may be ADF JSON or plain string).
 */
export function contentToHtml(content) {
  if (content == null) return '';
  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return adfToHtml(parsed);
      } catch {
        return escapeHtml(content);
      }
    }
    return escapeHtml(content);
  }
  return adfToHtml(content);
}
